import React from "react";
import { motion } from "framer-motion";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { HomeHeroCarousel } from "./HomeHeroCarousel";
import { MarketingFooter, MarketingHeader } from "./MarketingChrome";
import { ServiceMicroIllustration } from "./ServiceMicroIllustration";

const services = [
  {
    title: "BIM 3D + Synthèse",
    description: "Production de maquettes numériques, coordination, détection de conflits et suivi des maquettes.",
    eyebrow: "Production + coordination",
    chip: "3D + clash",
    illustration: "bim3d-synthesis",
    testId: "marketing-service-card-bim3d-synthesis",
  },
  {
    title: "BIM 4D + Planning",
    description: "Liaison planning-maquette, phasage chantier, simulation visuelle et pilotage du temps.",
    eyebrow: "Temps + séquences",
    chip: "4D + gantt",
    illustration: "bim4d-planning",
    testId: "marketing-service-card-bim4d-planning",
  },
  {
    title: "Formations",
    description: "Formations BIM orientées production, coordination, outils métier et montée en compétence.",
    eyebrow: "Transmission",
    chip: "Montée en compétence",
    illustration: "training",
    testId: "marketing-service-card-formations",
  },
  {
    title: "Logiciels BIM",
    description: "Outils connectés développés par Bimcubes pour relier BIM, données, planning et patrimoine immobilier.",
    eyebrow: "Outils connectés",
    chip: "Data + patrimoine",
    illustration: "software",
    testId: "marketing-service-card-software",
  },
];

const badges = ["Production BIM", "Coordination & synthèse", "Logiciels métier"];

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: index * 0.08, ease: "easeOut" },
  }),
};

export default function LandingPage() {
  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(190,242,100,0.14),_transparent_24%),linear-gradient(180deg,_#fbfdf8_0%,_#f8fcf3_44%,_#ffffff_100%)]" data-testid="marketing-home-page">
      <MarketingHeader />

      <main>
        <section className="relative overflow-hidden px-4 pb-16 pt-12 sm:px-6 lg:px-8 lg:pb-24 lg:pt-20" data-testid="marketing-hero-section">
          <div className="absolute inset-x-0 top-0 h-[30rem] bg-[radial-gradient(circle_at_top,_rgba(45,212,191,0.12),_transparent_42%)]" />
          <div className="absolute left-[6%] top-[18%] h-48 w-48 rounded-full bg-[#d9f99d]/30 blur-3xl" />
          <div className="absolute right-[10%] top-[22%] h-56 w-56 rounded-full bg-[#99f6e4]/25 blur-3xl" />

          <div className="mx-auto max-w-7xl">
            <motion.div animate="show" className="relative z-10 mx-auto max-w-5xl text-center" initial="hidden" variants={fadeUp}>
              <Badge className="rounded-full border border-[#b7e4c7] bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.24em] text-[#0f766e] shadow-[0_16px_40px_-32px_rgba(15,118,110,0.65)]" data-testid="marketing-hero-eyebrow">
                Bureau d’études BIM + logiciels connectés
              </Badge>

              <motion.h1
                className="bim-display mt-8 text-4xl font-semibold leading-[1.02] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl"
                custom={1}
                data-testid="marketing-hero-title"
                variants={fadeUp}
              >
                Bimcubes — le BIM, de la maquette numérique au pilotage de projet
              </motion.h1>

              <motion.p
                className="mx-auto mt-8 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg"
                custom={2}
                data-testid="marketing-hero-subtitle"
                variants={fadeUp}
              >
                Maquettes numériques, synthèse BIM, BIM 4D, planning chantier, formations et logiciels : tout le BIM réuni pour accompagner vos projets de construction.
              </motion.p>
            </motion.div>
          </div>

          <motion.div animate="show" className="mt-14 flex justify-center lg:mt-16" initial="hidden" variants={fadeUp}>
            <HomeHeroCarousel />
          </motion.div>
        </section>

        <section className="relative px-4 py-18 sm:px-6 lg:px-8 lg:py-28" data-testid="marketing-services-section" id="services">
          <div className="absolute inset-x-0 top-0 h-48 bg-[radial-gradient(circle_at_top,_rgba(163,230,53,0.1),_transparent_58%)]" />
          <div className="mx-auto max-w-[1560px] rounded-[2.9rem] border border-[#d9e7d5]/85 bg-[linear-gradient(180deg,_rgba(255,255,255,0.78)_0%,_rgba(248,252,243,0.98)_100%)] px-8 py-10 shadow-[0_36px_96px_-58px_rgba(15,118,110,0.24)] sm:px-11 sm:py-12 lg:px-14 lg:py-14">
            <motion.div animate="show" initial="hidden" variants={fadeUp}>
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="marketing-services-eyebrow">
                Tout le BIM réuni
              </div>
              <h2 className="bim-display mt-4 max-w-4xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="marketing-services-title">
                Quatre grandes rubriques pour relier BIM, planning, formation et logiciels métier.
              </h2>
              <p className="mt-5 max-w-4xl text-lg leading-9 text-slate-600" data-testid="marketing-services-subtitle">
                Une lecture plus claire, directement alignée avec le header, tout en gardant la même palette et la même qualité visuelle que le hero.
              </p>
            </motion.div>

            <div className="mt-14 grid gap-8 md:grid-cols-2 xl:grid-cols-4" data-testid="marketing-services-grid">
              {services.map((service, index) => {
                return (
                  <motion.div animate="show" custom={index + 1} initial="hidden" key={service.title} variants={fadeUp}>
                    <Card className="group h-full rounded-[2.2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_28px_76px_-50px_rgba(15,118,110,0.35)] backdrop-blur-xl transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_36px_96px_-44px_rgba(15,118,110,0.4)]" data-testid={service.testId}>
                      <CardContent className="p-9 sm:p-10">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]" data-testid={`${service.testId}-eyebrow`}>
                              {service.eyebrow}
                            </div>
                            <h3 className="bim-display mt-4 text-[1.35rem] font-semibold text-slate-950" data-testid={`${service.testId}-title`}>
                              {service.title}
                            </h3>
                          </div>
                          <div className="rounded-full border border-white/70 bg-white/65 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0f766e] shadow-[0_12px_30px_-24px_rgba(15,118,110,0.35)]" data-testid={`${service.testId}-chip`}>
                            {service.chip}
                          </div>
                        </div>

                        <div className="mt-6">
                          <ServiceMicroIllustration testId={`${service.testId}-illustration`} type={service.illustration} />
                        </div>

                        <p className="mt-6 text-base leading-8 text-slate-600" data-testid={`${service.testId}-body`}>
                          {service.description}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 pb-10 pt-6 sm:px-6 lg:px-8 lg:pb-20" data-testid="marketing-about-section">
          <div className="mx-auto max-w-[1520px] rounded-[2.8rem] border border-[#d9e7d5]/85 bg-[linear-gradient(140deg,_rgba(255,255,255,0.95)_0%,_rgba(240,253,244,0.95)_38%,_rgba(254,252,232,0.92)_100%)] p-11 shadow-[0_36px_96px_-58px_rgba(15,118,110,0.24)] sm:p-13 lg:p-16">
            <div className="absolute pointer-events-none" />
            <div className="mx-auto max-w-4xl text-center">
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="marketing-about-eyebrow">
                Qui est Bimcubes ?
              </div>
              <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="marketing-about-title">
                Bureau d’études BIM & éditeur de logiciels
              </h2>
              <p className="mt-6 text-lg leading-9 text-slate-600" data-testid="marketing-about-description">
                Bimcubes accompagne les acteurs de la construction dans la production de maquettes numériques, la coordination de projet, le BIM 4D, le planning chantier et le développement d’outils numériques connectés aux données du bâtiment.
              </p>
              <p className="mt-4 text-lg leading-9 text-slate-600" data-testid="marketing-about-mission">
                Notre objectif : transformer la maquette BIM en véritable outil de pilotage, depuis la conception jusqu’à l’exploitation du patrimoine immobilier.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4" data-testid="marketing-about-badges">
                {badges.map((badge, index) => (
                  <Badge
                    className="rounded-full border border-[#d9e7d5] bg-white/88 px-5 py-2.5 text-sm font-medium text-[#0f766e] shadow-[0_18px_35px_-28px_rgba(15,118,110,0.24)]"
                    data-testid={`marketing-about-badge-${index + 1}`}
                    key={badge}
                    variant="outline"
                  >
                    {badge}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}